import { Zap, Download, Chrome, ToggleRight, FolderOpen, UploadCloud, CheckCircle2 } from 'lucide-react';
import { Button } from '@components/ui/Button';

export function ExtensionSettingsPage() {
  const steps = [
    {
      title: 'Extract the Archive',
      icon: FolderOpen,
      description: (
        <>
          Locate the downloaded <code className="bg-khiimori-bg-tertiary px-1.5 py-0.5 rounded text-primary-400 font-mono text-xs">khiimori-extension.zip</code> file on your computer and extract its contents to a permanent local folder.
        </>
      ),
    },
    {
      title: 'Open Extensions Page',
      icon: Chrome,
      description: (
        <>
          In your browser address bar, navigate to <code className="bg-khiimori-bg-tertiary px-1.5 py-0.5 rounded text-primary-400 font-mono text-xs">chrome://extensions</code> (or <code className="bg-khiimori-bg-tertiary px-1.5 py-0.5 rounded text-primary-400 font-mono text-xs">edge://extensions</code>).
        </>
      ),
    },
    {
      title: 'Enable Developer Mode',
      icon: ToggleRight,
      description: 'Turn on the "Developer mode" toggle located in the top-right corner of the Extensions dashboard page.',
    },
    {
      title: 'Load Unpacked Extension',
      icon: UploadCloud,
      description: 'Click the "Load unpacked" action button in the top action bar and select the unzipped directory from Step 1.',
    },
    {
      title: 'Ready to Go! Pin to Toolbar',
      icon: CheckCircle2,
      description: 'The Khiimori Companion extension is now active. Pin it to your browser toolbar for instant sync monitoring and quick actions.',
      status: true,
    }
  ];

  return (
    <div className="w-full max-w-full min-h-0 px-4 pt-6 pb-32 sm:px-6 lg:px-8 lg:pt-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="mx-auto w-full max-w-5xl">
        
        {/* Header */}
        <div className="mb-12 text-center flex flex-col items-center">
          <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-primary-500/10 mb-4 ring-1 ring-primary-500/20 shadow-[0_0_30px_rgba(139,92,246,0.15)]">
            <Zap className="w-6 h-6 text-primary-400" />
          </div>
          <h1 className="text-2xl font-bold text-khiimori-text-primary tracking-tight mb-3">Khiimori Browser Extension</h1>
          <p className="text-khiimori-text-muted text-sm max-w-2xl leading-relaxed">
            Supercharge your research workflows with bidirectional tab synchronization, intelligent context capture, and instant command palette actions directly inside your browser.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          
          {/* Left Column */}
          <div className="lg:col-span-5 space-y-8">
            
            {/* Download Section */}
            <div>
              <div className="text-[10px] font-bold tracking-widest text-primary-400/80 uppercase mb-3 px-1">
                Manual Installation Package
              </div>
              <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-2xl p-6 shadow-lg text-center relative overflow-hidden group">
                {/* Decorative background glow behind the icon */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-24 bg-primary-500/20 blur-2xl rounded-full transition-transform duration-500 group-hover:scale-150"></div>
                
                <div className="relative z-10 flex flex-col items-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-khiimori-bg-elevated to-khiimori-bg-tertiary border border-khiimori-border/20 shadow-xl flex items-center justify-center mb-4 p-2 relative">
                    <img src="/brand/icon-128.png" alt="Khiimori Extension" className="w-full h-full object-contain drop-shadow-[0_0_10px_rgba(139,92,246,0.5)]" />
                  </div>
                  
                  <h2 className="text-lg font-bold text-khiimori-text-primary mb-2">Get the Extension</h2>
                  <p className="text-xs text-khiimori-text-muted mb-6 leading-relaxed max-w-[260px] mx-auto">
                    Download the latest pre-compiled build ready for developer load unpacked installation.
                  </p>
                  
                  <Button
                    size="lg"
                    className="w-full bg-primary-600 hover:bg-primary-500 text-white font-medium border-0 shadow-lg shadow-primary-500/20 mb-3 transition-all group-hover:shadow-primary-500/40"
                    onClick={() => {
                      window.location.href = '/khiimori-extension.zip';
                    }}
                    leftIcon={<Download className="w-4 h-4" />}
                  >
                    Download .zip Package
                  </Button>
                  
                  <div className="text-center text-[11px] text-khiimori-text-secondary/50 font-medium tracking-wide">
                    Version 1.0.0 • 68 KB (Unpacked: 210 KB)
                  </div>
                </div>
                
                <div className="pt-5 border-t border-khiimori-border/10">
                  <div className="text-[11px] text-khiimori-text-muted mb-3 font-medium">Supported Chromium Environments</div>
                  <div className="flex flex-wrap gap-2">
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-khiimori-bg-tertiary/50 border border-khiimori-border/5 text-[11px] text-khiimori-text-secondary">
                      <Chrome className="w-3.5 h-3.5 text-blue-400" /> Google Chrome
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-khiimori-bg-tertiary/50 border border-khiimori-border/5 text-[11px] text-khiimori-text-secondary">
                      <Chrome className="w-3.5 h-3.5 text-sky-400" /> Microsoft Edge
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sync Preferences Section */}
            <div>
              <div className="text-[10px] font-bold tracking-widest text-khiimori-text-secondary/60 uppercase mb-3 px-1 flex items-center gap-2">
                Companion Sync Preferences
              </div>
              <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-2xl p-5 shadow-lg space-y-4">
                
                <div className="flex items-center justify-between group">
                  <div>
                    <div className="text-sm font-medium text-khiimori-text-primary mb-0.5 group-hover:text-primary-300 transition-colors">Continuous Tab Sync</div>
                    <div className="text-[11px] text-khiimori-text-muted">Stream open tabs into memory store</div>
                  </div>
                  <div className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none">
                    <span className="sr-only">Use setting</span>
                    <span aria-hidden="true" className="pointer-events-none absolute h-full w-full rounded-md bg-transparent"></span>
                    <span aria-hidden="true" className="pointer-events-none absolute mx-auto h-4 w-9 rounded-full bg-primary-500 transition-colors duration-200 ease-in-out"></span>
                    <span aria-hidden="true" className="pointer-events-none absolute left-0 inline-block h-5 w-5 translate-x-4 transform rounded-full border border-khiimori-bg-tertiary bg-white shadow ring-0 transition-transform duration-200 ease-in-out"></span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between group">
                  <div>
                    <div className="text-sm font-medium text-khiimori-text-primary mb-0.5 group-hover:text-primary-300 transition-colors">Local Context Capture</div>
                    <div className="text-[11px] text-khiimori-text-muted">Enable reading page markdown extraction</div>
                  </div>
                  <div className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none">
                    <span className="sr-only">Use setting</span>
                    <span aria-hidden="true" className="pointer-events-none absolute h-full w-full rounded-md bg-transparent"></span>
                    <span aria-hidden="true" className="pointer-events-none absolute mx-auto h-4 w-9 rounded-full bg-primary-500 transition-colors duration-200 ease-in-out"></span>
                    <span aria-hidden="true" className="pointer-events-none absolute left-0 inline-block h-5 w-5 translate-x-4 transform rounded-full border border-khiimori-bg-tertiary bg-white shadow ring-0 transition-transform duration-200 ease-in-out"></span>
                  </div>
                </div>
                
              </div>
            </div>

          </div>

          {/* Right Column: Installation Guide */}
          <div className="lg:col-span-7">
            <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-2xl p-6 shadow-lg h-full">
              
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-semibold text-khiimori-text-primary">Installation Guide</h2>
                <div className="px-2 py-0.5 rounded bg-primary-500/10 text-primary-400 text-[10px] font-bold uppercase tracking-wider border border-primary-500/20">
                  5 Steps
                </div>
              </div>
              
              <p className="text-xs text-khiimori-text-muted mb-8 pb-4 border-b border-khiimori-border/5">
                Complete these simple steps to mount the extension into your browser.
              </p>
              
              <div className="space-y-6">
                {steps.map((step, idx) => (
                  <div key={idx} className="flex gap-4 group justify-between items-start">
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center mt-0.5">
                        <div className="w-8 h-8 rounded-full bg-primary-500/10 border border-primary-500/20 flex items-center justify-center shrink-0 group-hover:bg-primary-500/20 transition-colors shadow-[0_0_10px_rgba(139,92,246,0.1)]">
                          <span className="text-xs font-bold text-primary-400">{idx + 1}</span>
                        </div>
                        {idx !== steps.length - 1 && (
                          <div className="w-[1px] h-full bg-gradient-to-b from-primary-500/20 to-transparent mt-2 mb-1 group-hover:from-primary-500/40 transition-colors"></div>
                        )}
                      </div>
                      
                      <div className="pb-2">
                        <h3 className="text-sm font-semibold text-khiimori-text-primary mb-1.5 group-hover:text-primary-300 transition-colors">
                          {step.title}
                        </h3>
                        <p className="text-xs text-khiimori-text-muted leading-relaxed max-w-lg">
                          {step.description}
                        </p>
                        
                        {step.status && (
                          <div className="mt-3 flex items-center gap-1.5 text-[10px] font-medium text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-md w-fit border border-emerald-500/20">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                            Khiimori Core v1.0 connected
                          </div>
                        )}
                      </div>
                    </div>
                    {step.icon && (
                      <div className="mt-1 flex-shrink-0 text-khiimori-text-secondary/40 group-hover:text-primary-400/80 transition-colors">
                        <step.icon className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
}
